import { DATE_FORMAT, now, toMomentDate } from 'shovel-lib/utils/timeUtils';
import { CalendarEventType, Formats, PageRange, WorkloadTask } from 'shovel-lib/types';
import { Moment } from 'moment-timezone';
import { MissingReview, TaskPlanningInfo } from '@api/plannerApi';
import { pageRangesToDetected, ParsedPages } from '@components/course/taskEstimator/utils';
import { DEFAULT_SECONDS_PER_PAGE } from './constants/task';
import { minutesToDurationInHours } from './filters';
import { cardDates, dueDayLabel, inAppFormat, pastSessionLabel, upcomingDayLabel } from './dayLabel';

const SECONDS_IN_A_MINUTE = 60;

export const PLAN_THIS_WEEK_PROMPT_CHIPS = [
  "Don't study two different courses back-to-back",
  'Study 2 hours after trainings',
  'Spread work evenly across the week',
  'Prefer morning sessions',
  'Leave evenings free'
];

export type PlanThisWeekReasoningEffort = 'minimal' | 'low' | 'medium' | 'high';

export const PLAN_THIS_WEEK_REASONING_EFFORTS: { label: string; value: PlanThisWeekReasoningEffort }[] = [
  { label: 'minimal', value: 'minimal' },
  { label: 'low', value: 'low' },
  { label: 'medium (default)', value: 'medium' },
  { label: 'high', value: 'high' }
];

export const DEFAULT_PLAN_THIS_WEEK_REASONING_EFFORT: PlanThisWeekReasoningEffort = 'medium';

export type PlanningStyle = 'spread' | 'ahead' | 'deadline';
export type PlanningShape = 'even' | 'rampUp' | 'taperDown';
export type PlanningPreferredTime = 'morning' | 'afternoon' | 'evening';
export type PlanningWeekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

/** Every rule Auto-plan can break, in the order the student wants them broken. */
export type PlanningRule =
  | 'preferredTime'
  | 'break'
  | 'finishBy'
  | 'courseLightDays'
  | 'longestSession'
  | 'maxDaily'
  | 'startDate'
  | 'daysOff'
  | 'aroundSleep';

export type CourseRule = {
  courseId: number;
  /** Days for this course's tasks of 45 min or less: its deep work goes on other days when there's room. */
  lightDays: PlanningWeekday[];
  /** Empty means "same as the general preference". */
  preferredTime: PlanningPreferredTime[];
};

/** Stored in user settings (`planningPreferences`); the ai-service scheduler reads the same shape. */
export type PlanningPreferences = {
  style: PlanningStyle;
  shape: PlanningShape;
  /**
   * The length deep work sessions aim for; a task up to half as long again stays in one session. The longest
   * sitting on one task is twice it, which the scheduler works out for itself.
   */
  idealSession: number;
  finishDaysAhead: number;
  /** The break after deep work: a session of a task over 45 minutes. */
  deepBreakMinutes: number;
  /** The break after a small task, 45 minutes or less. */
  smallBreakMinutes: number;
  /**
   * The break after a class or other event, to get to a seat. Not after an event with a commute after it: the commute
   * is that time.
   */
  eventBreakMinutes: number;
  /** No study in this much time after waking up, the start of the student's awake time. */
  startAfterWakingMinutes: number;
  /** No study in this much time before each bedtime, the end of the student's awake time. */
  stopBeforeBedMinutes: number;
  /** Empty means no preference. */
  preferredTime: PlanningPreferredTime[];
  daysOff: PlanningWeekday[];
  maxDailyMinutes: number | null;
  /**
   * Two short recall reviews after each reading, made as tasks when Auto-plan plans their dates: the day after it is
   * due and about a week later. The app makes them; the scheduler plans them like any small task.
   */
  addReviews: boolean;
  /**
   * Work through courses in `courseOrder` inside a study block: Math while you are fresh, History after. Only an
   * order - it never moves work to another block or day. Turning it off keeps the order for next time.
   */
  orderCourses: boolean;
  /**
   * When not everything fits, after tasks flagged High and Medium have had their time: what's due soonest first, a
   * half-hour start for each task due in these dates and then what's due soonest, or some time for each. Flagged Low
   * goes last.
   */
  whenShort: PlanningWhenShort;
  /** Course ids, first first. */
  courseOrder: number[];
  courseRules: CourseRule[];
  /** Most important first. Missing rules fall back to the default ranking. */
  rulePriority: PlanningRule[];
};

export const DEFAULT_PLANNING_PREFERENCES: PlanningPreferences = {
  style: 'spread',
  shape: 'even',
  idealSession: 90,
  finishDaysAhead: 1,
  deepBreakMinutes: 15,
  smallBreakMinutes: 5,
  eventBreakMinutes: 10,
  startAfterWakingMinutes: 30,
  // 45 min: studying instead of winding down costs sleep, which costs memory (Petr, 2026-10-07; was 30 min).
  stopBeforeBedMinutes: 45,
  preferredTime: [],
  daysOff: [],
  // Five hours (Petr, 2026-10-07; was six): closer to what the research suggests, and it still gives way 30 minutes at a
  // time in a week that needs more.
  maxDailyMinutes: 300,
  // On (Petr, 2026-10-07): spaced recall is the best-supported technique there is, and the row already called it
  // recommended while it started off.
  addReviews: true,
  orderCourses: false,
  whenShort: 'startFirst',
  courseOrder: [],
  courseRules: [],
  rulePriority: []
};

/**
 * Most important at the top, the way the student is asked to rank them. The scheduler breaks from
 * the other end and is the only thing that reads this backwards.
 */
// A task's start date gives way first of the rules that decide what fits (Petr, 2026-10-07: getting things done early
// is worth encouraging). The AI service's DEFAULT_RULE_PRIORITY is the same list.
export const DEFAULT_PLANNING_RULE_PRIORITY: PlanningRule[] = [
  'aroundSleep',
  'daysOff',
  'maxDaily',
  'longestSession',
  'finishBy',
  'break',
  'courseLightDays',
  'startDate',
  'preferredTime'
];

/**
 * The rules whose order actually decides anything.
 *
 * The scheduler enforces two different kinds of thing. These forbid: while one is on a slot simply is not
 * available, and dropping it creates room that did not exist. The two left out - preferredTime and finishBy -
 * only break ties between slots that were available either way, so dropping one frees nothing and its position
 * in the list changes no outcome. They stay in the order sent to the scheduler, and stay
 * settable in the sections that own them; they are only left out of the ranking.
 */
export const RANKABLE_PLANNING_RULES: PlanningRule[] = [
  'aroundSleep',
  'daysOff',
  'maxDaily',
  'longestSession',
  'break',
  'courseLightDays',
  'startDate'
];

export const isRankablePlanningRule = (rule: string): rule is PlanningRule =>
  RANKABLE_PLANNING_RULES.includes(rule as PlanningRule);

export const PLANNING_RULE_LABELS: Record<PlanningRule, string> = {
  preferredTime: 'Preferred times of day to study',
  break: 'Breaks',
  finishBy: 'When you like to finish a task',
  courseLightDays: "A course's light days",
  longestSession: 'Deep work session length',
  maxDaily: 'Most planned study time in a day',
  startDate: "A task's start date",
  daysOff: 'Days off',
  // Named for the two settings it stands for, under Breaks: "Time around sleep" read as a puzzle (Petr).
  aroundSleep: 'Time after waking & before bed'
};

/**
 * Whether these are Shovel's own defaults in everything that changes a plan, which is when "Reset to Shovel's
 * defaults" has nothing to do. A ranking spelled out in the default order, a course order kept while ordering is off,
 * and a course with no rules of its own all count as the default.
 */
export const isShovelDefaultPlanning = (prefs: PlanningPreferences) => {
  const defaults = DEFAULT_PLANNING_PREFERENCES;
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const compared = (Object.keys(defaults) as (keyof PlanningPreferences)[]).filter(
    key => key !== 'rulePriority' && key !== 'courseOrder' && key !== 'courseRules'
  );
  return (
    compared.every(key => same(prefs[key], defaults[key])) &&
    same(rankedPlanningRules(prefs), rankedPlanningRules(defaults)) &&
    prefs.courseRules.every(rule => rule.lightDays.length === 0 && rule.preferredTime.length === 0)
  );
};

/**
 * The student's ranking, with any rule they have never seen where Shovel ranks it: right after the nearest rule
 * ranked above it by default that their list has, or first when none is. Appended, a rule added after they saved
 * their ranking came last and gave way first - time around sleep, which Shovel ranks first, was the first thing a
 * crowded week took (Petr, 2026-10-07). The planner reads a saved ranking the same way.
 */
export const rankedPlanningRules = (prefs: PlanningPreferences): PlanningRule[] => {
  const ranked = prefs.rulePriority.filter(rule => DEFAULT_PLANNING_RULE_PRIORITY.includes(rule));
  DEFAULT_PLANNING_RULE_PRIORITY.forEach((rule, index) => {
    if (ranked.includes(rule)) return;
    const above = [...DEFAULT_PLANNING_RULE_PRIORITY.slice(0, index)].reverse().find(other => ranked.includes(other));
    ranked.splice(above ? ranked.indexOf(above) + 1 : 0, 0, rule);
  });
  return ranked;
};

/**
 * The rules that only choose between placements that would all have worked.
 *
 * The seven above decide what fits: while one is on, a slot simply is not available, and breaking it makes room
 * that did not exist. These two never do that. They pick which of the days and times that were free anyway the
 * work lands on, which is a different question and gets its own list. Batching is not among them: where it stands
 * is fixed, after filling the short gaps of a day, so there is nothing for the student to rank.
 */
export const SOFT_PLANNING_RULES: PlanningRule[] = ['finishBy', 'preferredTime'];

export const isSoftPlanningRule = (rule: string): rule is PlanningRule => SOFT_PLANNING_RULES.includes(rule as PlanningRule);

/** One of the two ranked groups, most important first - which is how they are stored. */
export const planningRulesByImportance = (
  prefs: PlanningPreferences,
  group: PlanningRule[] = RANKABLE_PLANNING_RULES
): PlanningRule[] => rankedPlanningRules(prefs).filter(rule => group.includes(rule));

/**
 * The full ranking again, for saving what the student dragged.
 *
 * Only one group is dragged at a time, so its order is spliced back into the full list rather than replacing it:
 * every rule outside that group keeps the exact position it already had.
 */
export const importanceToRulePriority = (
  byImportance: PlanningRule[],
  prefs: PlanningPreferences,
  group: PlanningRule[] = RANKABLE_PLANNING_RULES
): PlanningRule[] => {
  let next = 0;
  return rankedPlanningRules(prefs).map(rule =>
    group.includes(rule) && next < byImportance.length ? byImportance[next++] : rule
  );
};

/** Move a rule one place up or down a list. */
export const movePlanningRule = (order: PlanningRule[], rule: PlanningRule, by: -1 | 1): PlanningRule[] => {
  const from = order.indexOf(rule);
  const to = from + by;
  if (from < 0 || to < 0 || to >= order.length) return order;
  const next = [...order];
  next.splice(to, 0, ...next.splice(from, 1));
  return next;
};

export const PLANNING_STYLE_OPTIONS: { value: PlanningStyle; label: string; description: string }[] = [
  { value: 'ahead', label: 'Get ahead', description: 'Most sessions early, a few closer to your finish day' },
  { value: 'spread', label: 'Spread out', description: 'Evenly from the start date to your finish day' },
  {
    value: 'deadline',
    label: 'Close to deadline',
    description: 'A few sessions early, most close to your finish day'
  }
];

/** `summary` is how the Study sessions row reads it, where "stay similar" would not make sense on its own. */
export const PLANNING_SHAPE_OPTIONS: { value: PlanningShape; label: string; description: string; summary: string }[] = [
  { value: 'taperDown', label: 'Get shorter', description: 'Longest first, shortest last.', summary: 'getting shorter' },
  { value: 'even', label: 'Stay similar', description: 'Every session about your ideal length.', summary: 'similar length' },
  { value: 'rampUp', label: 'Get longer', description: 'Start short to ease in.', summary: 'getting longer' }
];

export const PLANNING_IDEAL_SESSION_OPTIONS = [45, 60, 90, 120, 180].map(minutes => ({
  value: minutes,
  label: formatPlanningMinutes(minutes)
}));

export const PLANNING_DEEP_BREAK_OPTIONS = [10, 15, 20, 30].map(minutes => ({
  value: minutes,
  label: formatPlanningMinutes(minutes)
}));

export const PLANNING_SMALL_BREAK_OPTIONS = [
  { value: 0, label: 'No break' },
  ...[5, 10].map(minutes => ({ value: minutes, label: formatPlanningMinutes(minutes) }))
];

export const PLANNING_EVENT_BREAK_OPTIONS = [
  { value: 0, label: 'No break' },
  ...[5, 10, 15, 30].map(minutes => ({ value: minutes, label: formatPlanningMinutes(minutes) }))
];

export const PLANNING_PREFERRED_TIME_OPTIONS: { value: PlanningPreferredTime; label: string; description?: string }[] = [
  { value: 'morning', label: 'Mornings', description: 'Before 12 pm' },
  { value: 'afternoon', label: 'Afternoons', description: '12 – 5 pm' },
  { value: 'evening', label: 'Evenings', description: 'After 5 pm' }
];

/** Multi-select toggle, kept in the order the options are listed. */
export const togglePreferredTime = (times: PlanningPreferredTime[], time: PlanningPreferredTime) => {
  const next = times.includes(time) ? times.filter(item => item !== time) : [...times, time];
  return PLANNING_PREFERRED_TIME_OPTIONS.filter(option => next.includes(option.value)).map(option => option.value);
};

export const PLANNING_WEEKDAY_OPTIONS: { value: PlanningWeekday; label: string }[] = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' }
];

/** The weekdays from the student's first day of the week, as their calendar shows them (0 = Sunday, 1 = Monday). */
export const planningWeekdayOptions = (firstDayOfWeek?: number | null) => {
  const start = ((firstDayOfWeek ?? 1) + 6) % 7;
  return [...PLANNING_WEEKDAY_OPTIONS.slice(start), ...PLANNING_WEEKDAY_OPTIONS.slice(0, start)];
};

// Up to an hour (Petr, 2026-10-07: 2h went, 45m came in). A saved 2h reads as the hour (`aroundSleepMinutes`).
export const PLANNING_AROUND_SLEEP_OPTIONS = [
  { value: 0, label: 'None' },
  ...[15, 30, 45, 60].map(minutes => ({ value: minutes, label: formatPlanningMinutes(minutes) }))
];

export const PLANNING_MAX_DAILY_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: 'No limit' },
  ...[120, 180, 240, 300, 360, 480].map(minutes => ({ value: minutes, label: formatPlanningMinutes(minutes) }))
];

/** How a broken rule reads inside a sentence. */
export const PLANNING_FINISH_AHEAD_OPTIONS = [
  { value: 2, label: 'Two days before' },
  { value: 1, label: 'The day before' },
  { value: 0, label: "On the day it's due" }
];

/** "Sep 20 – 26", or "Sep 29 – Oct 2" across months; `to` is the exclusive end of the visible range. */
export type PlanRangeEnd = { day: string; date: string };

/**
 * The dates being planned, as weekday and date apart, so a caller that can style them may set the
 * weekday back from the date it belongs to. `end` is absent for a single day.
 *
 * The end keeps the month only when it differs from the start's: with the weekday there to anchor
 * it, "Sun Sep 20 – Sat 26" is short and still unambiguous.
 */
export const planRangeEnds = (from: Date | string, to: Date | string): { start: PlanRangeEnd; end?: PlanRangeEnd } => {
  const start = toMomentDate(from);
  const end = toMomentDate(to).subtract(1, 'day');
  const head = { day: start.format('ddd'), date: start.format('MMM D') };
  if (start.isSame(end, 'day')) return { start: head };
  return {
    start: head,
    end: { day: end.format('ddd'), date: end.format(start.isSame(end, 'month') ? 'D' : 'MMM D') }
  };
};

export const formatPlanRange = (from: Date | string, to: Date | string) => {
  const { start, end } = planRangeEnds(from, to);
  const text = (part: PlanRangeEnd) => `${part.day} ${part.date}`;
  return end ? `${text(start)} – ${text(end)}` : text(start);
};

// The app's duration format ("2h 36m", "45m"), so Auto-plan reads like the rest of Shovel.
export function formatPlanningMinutes(minutes: number) {
  // The shared formatter pads minutes for "1h 05m"; on their own they read better unpadded ("5m", not "05m").
  return minutes > 0 && minutes < 60 ? `${minutes}m` : minutesToDurationInHours(minutes);
}

/**
 * The saved preferences, every one checked. The settings blob keeps whatever an earlier build saved - a preferred time
 * that was one word before it was a list, a shape since renamed, keys since dropped - and accounts on stage still hold
 * them: an old preferred time crashed the dialog (`.map` on a string), and the ai-service refuses a value or a key it
 * does not know. So each field is read for what it should be, converted where an old form is plain, and otherwise
 * left at its default.
 */
export const withPlanningDefaults = (saved?: unknown): PlanningPreferences => {
  const stored: Record<string, unknown> = saved && typeof saved === 'object' ? (saved as Record<string, unknown>) : {};
  const defaults = DEFAULT_PLANNING_PREFERENCES;
  const shape = typeof stored.shape === 'string' ? LEGACY_SHAPES[stored.shape] || stored.shape : undefined;
  return {
    style: oneOf(stored.style, PLANNING_STYLE_OPTIONS, defaults.style),
    shape: oneOf(shape, PLANNING_SHAPE_OPTIONS, defaults.shape),
    idealSession: wholeMinutes(stored.idealSession, 15, 480, defaults.idealSession),
    finishDaysAhead: wholeMinutes(stored.finishDaysAhead, 0, 7, defaults.finishDaysAhead),
    deepBreakMinutes: wholeMinutes(stored.deepBreakMinutes, 0, 120, defaults.deepBreakMinutes),
    smallBreakMinutes: wholeMinutes(stored.smallBreakMinutes, 0, 120, defaults.smallBreakMinutes),
    eventBreakMinutes: wholeMinutes(stored.eventBreakMinutes, 0, 120, defaults.eventBreakMinutes),
    startAfterWakingMinutes: aroundSleepMinutes(stored.startAfterWakingMinutes, defaults.startAfterWakingMinutes),
    stopBeforeBedMinutes: aroundSleepMinutes(stored.stopBeforeBedMinutes, defaults.stopBeforeBedMinutes),
    preferredTime: asPreferredTimes(stored.preferredTime),
    daysOff: asWeekdays(stored.daysOff),
    // "No limit" is a choice of its own, saved as null.
    maxDailyMinutes:
      stored.maxDailyMinutes === null ? null : wholeMinutes(stored.maxDailyMinutes, 30, 1440, defaults.maxDailyMinutes),
    addReviews: typeof stored.addReviews === 'boolean' ? stored.addReviews : defaults.addReviews,
    orderCourses: typeof stored.orderCourses === 'boolean' ? stored.orderCourses : defaults.orderCourses,
    whenShort: oneOf(stored.whenShort, PLANNING_WHEN_SHORT_OPTIONS, defaults.whenShort),
    courseOrder: Array.isArray(stored.courseOrder) ? stored.courseOrder.filter(Number.isInteger) : defaults.courseOrder,
    courseRules: (Array.isArray(stored.courseRules) ? stored.courseRules : [])
      .filter(rule => rule && typeof rule === 'object' && Number.isInteger(rule.courseId))
      .map(rule => ({
        courseId: rule.courseId,
        lightDays: asWeekdays(rule.lightDays),
        preferredTime: asPreferredTimes(rule.preferredTime)
      }))
      .filter(hasCourseRule),
    rulePriority: (Array.isArray(stored.rulePriority) ? stored.rulePriority : []).filter(rule =>
      DEFAULT_PLANNING_RULE_PRIORITY.includes(rule)
    )
  };
};

/**
 * The saved preferences `withPlanningDefaults` could not keep as they were: each with what was saved and what it reads
 * as now, undefined for a key Auto-plan no longer has. Empty when everything was kept. A preference never saved is not
 * here - filling it in with its default changes nothing the student chose.
 *
 * So the dialog can report what an older build left behind (to add conversions for what is common), and offer to save
 * the cleaned-up preferences, which stops the same values being read and reset at every opening.
 */
export const planningPreferenceChanges = (saved?: unknown): Record<string, { saved: unknown; now: unknown }> => {
  if (!saved || typeof saved !== 'object') return {};
  const clean: Record<string, unknown> = withPlanningDefaults(saved);
  return Object.fromEntries(
    Object.entries(saved as Record<string, unknown>)
      .filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(clean[key]))
      .map(([key, value]) => [key, { saved: value, now: clean[key] }])
  );
};

// Shapes saved under names they had before release. The chunk shapes gave way to the ideal session, which sizes a task
// on its own, so they fall back to the default.
const LEGACY_SHAPES: Record<string, PlanningShape> = { easeIn: 'rampUp', easeOff: 'taperDown' };

const oneOf = <T>(value: unknown, options: { value: T }[], fallback: T): T =>
  options.some(option => option.value === value) ? (value as T) : fallback;

const wholeMinutes = (value: unknown, least: number, most: number, fallback: number) =>
  typeof value === 'number' && Number.isInteger(value) && value >= least && value <= most ? value : fallback;

// The time kept clear after waking or before bed, as one of the choices now offered: a value saved from an older list
// (2h, before 2026-10-07) reads as the nearest, so a pill is always lit.
const aroundSleepMinutes = (value: unknown, fallback: number) => {
  const minutes = wholeMinutes(value, 0, 240, fallback);
  return PLANNING_AROUND_SLEEP_OPTIONS.reduce((best, option) =>
    Math.abs(option.value - minutes) < Math.abs(best.value - minutes) ? option : best
  ).value;
};

// A preferred time was one word ("morning", or "any" for none) before it could be several.
const asPreferredTimes = (value: unknown): PlanningPreferredTime[] => {
  const times = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
  return PLANNING_PREFERRED_TIME_OPTIONS.map(option => option.value).filter(time => times.includes(time));
};

const asWeekdays = (value: unknown): PlanningWeekday[] =>
  Array.isArray(value) ? PLANNING_WEEKDAY_OPTIONS.map(option => option.value).filter(day => value.includes(day)) : [];

export const hasCourseRule = (rule?: CourseRule) => !!rule && (rule.lightDays.length > 0 || rule.preferredTime.length > 0);

export const summarizeCourseRule = (rule?: CourseRule, firstDayOfWeek?: number | null) => {
  if (!rule || !hasCourseRule(rule)) return 'Nothing set';
  const days = rule.lightDays.length
    ? `light ${planningWeekdayOptions(firstDayOfWeek)
        .filter(option => rule.lightDays.includes(option.value))
        .map(option => option.label)
        .join(', ')}`
    : null;
  const time = rule.preferredTime.length
    ? rule.preferredTime.map(name => PLANNING_TIME_OF_DAY_PLURAL[name]).join(' & ')
    : null;
  // Time first, then days, to match the order the editor asks for them.
  const text = [time, days].filter(Boolean).join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** Replace one course's rule, dropping it when it no longer says anything. */
export const setCourseRule = (rules: CourseRule[], next: CourseRule) => [
  ...rules.filter(rule => rule.courseId !== next.courseId),
  ...(hasCourseRule(next) ? [next] : [])
];

const PLANNING_TIME_OF_DAY_PLURAL: Record<PlanningPreferredTime, string> = {
  morning: 'mornings',
  afternoon: 'afternoons',
  evening: 'evenings'
};

export const toggleWeekday = (days: PlanningWeekday[], day: PlanningWeekday) =>
  days.includes(day)
    ? days.filter(value => value !== day)
    : PLANNING_WEEKDAY_OPTIONS.map(option => option.value).filter(value => value === day || days.includes(value));

type UnplannedWork = { taskId: number; minutes: number };

// The student's own date and clock settings ("7 Oct", "13:00") through `inAppFormat`, so the dialog's dates read like
// the task cards beside the calendar (Petr: "Due: Oct 7, 1:00pm" here, "Oct 7, 13:00" there).
/** "Oct 7". */
export const formatPlanDate = (at: Moment) => at.format(inAppFormat(Formats.MONTH_DAY_FORMAT, 'MMM D'));
/** A card's Due and (while it's ahead) Start lines, as the sidebar's cards give them (utils/dayLabel). */
const cardDatesOf = (dueAt: Moment, daysAhead?: number | null) => {
  const { due, start } = cardDates(dueAt, daysAhead);
  return { due, start: start || undefined };
};

/** What a task's card in the sidebar reads its time label from (`PlannedTag`), so an Overdue card's says the same. */
export type CardTime = { timeNeed: number; timePlanned: number; timeSpent: number };

type CardTimeFields = Partial<Pick<TaskPlanningInfo, 'timeNeed' | 'timePlanned' | 'timeSpent'>>;

const cardTimeOf = (task: CardTimeFields | undefined, fallbackNeed: number): CardTime => ({
  timeNeed: task?.timeNeed ?? fallbackNeed,
  timePlanned: task?.timePlanned ?? 0,
  timeSpent: task?.timeSpent ?? 0
});

export type PlanOutcomeRow = {
  taskId: number;
  title: string;
  courseId?: number;
  courseName?: string;
  // Formatted for the task card: when the planning window opens, and when the task is due.
  start?: string;
  due: string;
  // Time still needed; split below into what is planned now, left for later dates, and won't fit.
  estimate: number;
  now: number;
  later: number;
  // Only work for a task due within these dates counts as won't fit: what a task due later loses here can still be
  // planned when those dates are planned, so it is counted as later.
  wontFit: number;
  wontFitReason?: string;
};

/** "Oct 10": the last day of the planned range, whose exclusive end is `to`. */
export const formatPlanRangeEnd = (to: Date | string) => formatPlanDate(toMomentDate(to).subtract(1, 'day'));

/**
 * The dialog's per-task outcome, by due date. Without a preview (`plannedByTask` null) nothing is split yet. Tasks
 * with no free time before they are due only appear in `noTime`, so they are looked up in `tasksInRange`.
 */
export const describePlanOutcome = ({
  tasks,
  noTime,
  tasksInRange,
  plannedByTask,
  unplanned,
  brokenRules,
  rangeEnd,
  timeZone
}: {
  tasks: {
    id: number;
    title: string;
    courseId?: number;
    courseName?: string;
    dueDate: Date | string;
    earliestStart?: Date | string;
    timeNeed: number;
  }[];
  noTime: UnplannedWork[];
  tasksInRange: Pick<TaskPlanningInfo, 'id' | 'title' | 'courseId' | 'courseName' | 'dueDate' | 'daysToStartAhead'>[];
  plannedByTask: Record<number, number> | null;
  unplanned: UnplannedWork[];
  brokenRules: string[];
  // Exclusive end of the planned range.
  rangeEnd: Date | string;
  timeZone: string;
}): PlanOutcomeRow[] => {
  const unplannedByTask = new Map<number, number>();
  [...unplanned, ...noTime].forEach(item =>
    unplannedByTask.set(item.taskId, (unplannedByTask.get(item.taskId) || 0) + item.minutes)
  );
  const inRangeById = new Map(tasksInRange.map(task => [task.id, task]));
  const all = [
    ...tasks,
    ...noTime
      .filter(item => !tasks.some(task => task.id === item.taskId) && inRangeById.has(item.taskId))
      .map(item => {
        const task = inRangeById.get(item.taskId)!;
        return {
          ...task,
          timeNeed: item.minutes,
          // These tasks never reached the scheduler, so their window start is taken from the task itself.
          earliestStart: toMomentDate(task.dueDate)
            .subtract(task.daysToStartAhead, 'days')
            .toDate()
        };
      })
  ];
  const rangeEndsAt = toMomentDate(rangeEnd);
  return all
    .map(task => {
      const dueAt = toMomentDate(task.dueDate);
      const noTimeTask = noTime.some(item => item.taskId === task.id);
      const missing = plannedByTask || noTimeTask ? Math.min(task.timeNeed, unplannedByTask.get(task.id) || 0) : 0;
      const dueHere = dueAt.isBefore(rangeEndsAt);
      const wontFit = dueHere ? missing : 0;
      const now = plannedByTask ? Math.min(task.timeNeed - wontFit, plannedByTask[task.id] || 0) : 0;
      const due = dueDayLabel(dueAt.tz(timeZone)).short;
      const startsAt = 'earliestStart' in task && task.earliestStart ? toMomentDate(task.earliestStart) : null;
      const later = plannedByTask ? Math.max(0, task.timeNeed - now - wontFit) : 0;
      const row: PlanOutcomeRow = {
        taskId: task.id,
        title: task.title,
        courseId: task.courseId,
        courseName: task.courseName,
        // No Start line on the due day itself, which the Due line already gives.
        start: (startsAt && !startsAt.isSame(dueAt, 'day') && upcomingDayLabel(startsAt.tz(timeZone))) || undefined,
        due,
        estimate: task.timeNeed,
        now,
        later,
        wontFit,
        // Every rule bends before work is left out, so what is left is always a shortage of
        // time. Whether any of the task fits decides the wording: none at all is a different
        // problem from not enough.
        wontFitReason: wontFit
          ? now > 0 || later > 0
            ? "Not enough free study time before it's due."
            : "No free study time before it's due."
          : undefined
      };
      return { row, dueAt };
    })
    .sort((a, b) => a.dueAt.valueOf() - b.dueAt.valueOf() || a.row.title.localeCompare(b.row.title))
    .map(({ row }) => row);
};

// Quick picks for estimating a task straight from the Plan dialog. They stop one short of the estimator's
// longest pick so the row and its Custom pill stay on one line in the narrower pane. Readings have no picks:
// a student counts a chapter's pages and types them.
export const PLAN_ESTIMATE_OPTIONS = [15, 30, 60, 120, 180];

// The same picks and 5h, everywhere a task or a category is estimated outside the Plan dialog: the sidebar's Estimate and
// +time popups, the Task Estimator's rows, and a category's time in Course details (Petr, 2026-10-29).
export const TASK_ESTIMATE_OPTIONS = [...PLAN_ESTIMATE_OPTIONS, 300];

// What a reading task needs instead of minutes: it is estimated in pages, at its source's rate.
// No page count comes with it — a reading whose pages add up is estimated, so it is never in this list.
export type UnestimatedReading = {
  sourceName: string;
  // Falls back to DEFAULT_SECONDS_PER_PAGE. The preview is ours, the estimate the server keeps is its own.
  secondsPerPage: number;
};

export type UnestimatedRow = {
  taskId: number;
  title: string;
  courseId?: number;
  courseName?: string;
  /** When the task's planning window opens. Absent only when the task has no due date to count back from. */
  start?: string;
  due: string;
  // Set when the task has a reading source, and the row asks for pages instead of time.
  reading?: UnestimatedReading;
};

/**
 * A reading task counts as estimated once its page ranges add up, whatever its timeNeed says.
 *
 * Time already spent settles the other case: a task with nothing left to do has been worked, not left
 * unestimated, and asking a student to estimate work they have just finished reads as a mistake.
 */
const isUnestimated = (timeNeed: number | undefined, workload?: WorkloadEstimateFields) =>
  workload?.reading ? pageRangesToDetected(workload.pageRanges).pages == null : !timeNeed && !workload?.timeSpent;

// timeSpent is optional: callers without the workload store fall back to judging on time alone, as before.
type WorkloadEstimateFields = Pick<WorkloadTask, 'id' | 'reading' | 'pageRanges'> & { timeSpent?: number };

/**
 * Tasks Auto-plan has to skip: they are in range but have no estimate yet.
 *
 * `workloadTasks` is what tells a reading apart from a plain task — the planner endpoint doesn't say. Left
 * out (or missing this task), every task is judged on timeNeed alone, the way it was before readings.
 */
export const describeUnestimated = (
  // `daysToStartAhead` is optional so a caller that has no window to give still gets rows; missing
  // reads as none, which puts the window's start on the due day, the way the rest of the app does.
  tasks: (Pick<TaskPlanningInfo, 'id' | 'title' | 'courseId' | 'courseName' | 'dueDate' | 'timeNeed'> &
    Partial<Pick<TaskPlanningInfo, 'daysToStartAhead'>>)[],
  timeZone: string,
  workloadTasks: WorkloadEstimateFields[] = []
): UnestimatedRow[] => {
  const byId = new Map(workloadTasks.map(task => [task.id, task]));
  return tasks
    .filter(task => isUnestimated(task.timeNeed, byId.get(task.id)))
    .map(task => ({ task, dueAt: toMomentDate(task.dueDate) }))
    .sort((a, b) => a.dueAt.valueOf() - b.dueAt.valueOf() || a.task.title.localeCompare(b.task.title))
    .map(({ task, dueAt }) => {
      const reading = byId.get(task.id)?.reading;
      return {
        taskId: task.id,
        title: task.title,
        courseId: task.courseId,
        courseName: task.courseName,
        // Counted back from the due date, the way `describePlanOutcome` does it for a task that never
        // reached the scheduler: an unestimated one never does, so there is no `earliestStart` to read.
        ...cardDatesOf(dueAt.tz(timeZone), task.daysToStartAhead),
        reading: reading && {
          sourceName: reading.name,
          secondsPerPage: reading.secondsPerPage || DEFAULT_SECONDS_PER_PAGE
        }
      };
    });
};

/** One fresh range for the page-ranges endpoint, which replaces whatever the task had. */
export const toPageRanges = (parsed: ParsedPages): PageRange[] =>
  parsed.from != null && parsed.to != null
    ? [{ from: parsed.from, to: parsed.to, pages: parsed.pages }]
    : [{ pages: parsed.pages }];

/**
 * A reading's ranges with more pages after them, as the task popup's Pages field adds them: a range of its own beside
 * the ones it had, never stretching one (Petr, 2026-10-22). The ranges it had go back as they came, ids included, since
 * the endpoint replaces the whole list.
 */
export const withAddedPages = (current: PageRange[], parsed: ParsedPages): PageRange[] => [
  ...current,
  ...toPageRanges(parsed)
];

export type PlanOutcomeGroupKey = 'wontFit' | 'partly' | 'now' | 'nowLater' | 'later' | 'pending';

export type PlanOutcomeGroup = {
  key: PlanOutcomeGroupKey;
  title: string;
  rows: PlanOutcomeRow[];
  // What the header states on the right: the minutes this group is about.
  minutes: number;
  // Set by the two groups that place only part of a task ("Partially fit", "Split between now & later"): what
  // the tasks need in total, to set `minutes` against.
  ofMinutes?: number;
  atRisk: boolean;
};

/**
 * The task list, grouped by what Auto-plan will do with each task. Work the scheduler leaves for later dates is on
 * track, not partial, so it has its own group and is never framed as a problem. Empty groups are dropped.
 */
export const groupPlanOutcome = (rows: PlanOutcomeRow[], split: boolean): PlanOutcomeGroup[] => {
  const sum = (group: PlanOutcomeRow[], key: 'now' | 'later' | 'wontFit' | 'estimate') =>
    group.reduce((total, row) => total + row[key], 0);

  // Until the preview splits them, every task is simply in range.
  if (!split) {
    const pending = rows.filter(row => row.wontFit === 0);
    const noTime = rows.filter(row => row.wontFit > 0);
    return [
      noTime.length
        ? { key: 'wontFit' as const, title: "Won't fit", rows: noTime, minutes: sum(noTime, 'wontFit'), atRisk: true }
        : null,
      pending.length
        ? {
            key: 'pending' as const,
            title: 'Tasks',
            rows: pending,
            minutes: sum(pending, 'estimate'),
            atRisk: false
          }
        : null
    ].filter(Boolean) as PlanOutcomeGroup[];
  }

  // Two questions, kept apart: the first two groups are about risk ("fit"), the rest about what actually
  // lands in these dates ("planned"). Splitting the safe rows on what is left over, not on whether anything
  // is planned now, is what stops a part-planned task claiming to be done.
  const wontFit = rows.filter(row => row.wontFit > 0 && row.now === 0 && row.later === 0);
  const partly = rows.filter(row => row.wontFit > 0 && (row.now > 0 || row.later > 0));
  const now = rows.filter(row => row.wontFit === 0 && row.now > 0 && row.later === 0);
  const nowLater = rows.filter(row => row.wontFit === 0 && row.now > 0 && row.later > 0);
  const later = rows.filter(row => row.wontFit === 0 && row.now === 0 && row.later > 0);

  return [
    { key: 'wontFit' as const, title: "Won't fit", rows: wontFit, minutes: sum(wontFit, 'wontFit'), atRisk: true },
    {
      key: 'partly' as const,
      title: 'Partially fit',
      rows: partly,
      minutes: sum(partly, 'now') + sum(partly, 'later'),
      ofMinutes: sum(partly, 'estimate'),
      atRisk: true
    },
    { key: 'now' as const, title: 'Fully fit', rows: now, minutes: sum(now, 'now'), atRisk: false },
    {
      key: 'nowLater' as const,
      title: 'Split between now & later',
      rows: nowLater,
      minutes: sum(nowLater, 'now'),
      ofMinutes: sum(nowLater, 'estimate'),
      atRisk: false
    },
    { key: 'later' as const, title: 'Left for later', rows: later, minutes: sum(later, 'later'), atRisk: false }
  ].filter(group => group.rows.length > 0);
};

/** What Auto-plan had to bend to fit the work, for the line next to the week's fixes. */
// Short values for the preference rows in the Plan dialog.
export const summarizePlanningStyle = (prefs: PlanningPreferences) => {
  const style = PLANNING_STYLE_OPTIONS.find(option => option.value === prefs.style)?.label;
  const finish = PLANNING_FINISH_AHEAD_OPTIONS.find(option => option.value === prefs.finishDaysAhead)?.label;
  return [style, finish && `finish ${finish.toLowerCase()}`].filter(Boolean).join(' · ');
};

export const summarizePlanningSessions = (prefs: PlanningPreferences) =>
  [
    `${formatPlanningMinutes(prefs.idealSession)} sessions`,
    PLANNING_SHAPE_OPTIONS.find(option => option.value === prefs.shape)?.summary
  ]
    .filter(Boolean)
    .join(' · ');

/** "On" or "Off": the row is how a student finds a setting that starts off. */
export const summarizePlanningReviews = (prefs: PlanningPreferences) => (prefs.addReviews ? 'On' : 'Off');

export type PlanningWhenShort = 'dueFirst' | 'startFirst' | 'shareTime';

export const PLANNING_WHEN_SHORT_OPTIONS: { value: PlanningWhenShort; label: string; description: string }[] = [
  { value: 'dueFirst', label: 'Due soonest first', description: 'The task due soonest gets all its time first.' },
  {
    value: 'startFirst',
    label: 'Start everything',
    description: "Each task due in these dates gets a 30-min start, then the rest goes to what's due soonest."
  },
  {
    value: 'shareTime',
    label: 'Some time for each',
    description: 'Each task due in these dates gets part of its time, bigger tasks more.'
  }
];

export const summarizePlanningWhenShort = (prefs: PlanningPreferences) =>
  PLANNING_WHEN_SHORT_OPTIONS.find(option => option.value === prefs.whenShort)?.label || '';

/** The break after deep work and the time kept clear around sleep, the two a student is likeliest to change. */
// Two parts at most, like every closed row (Petr: less cluttered): the deep work break, then the time around sleep as
// one part - "30m / 45m" for after waking and before bed when both are kept and differ.
export const summarizePlanningBreaks = (prefs: PlanningPreferences) => {
  const waking = prefs.startAfterWakingMinutes;
  const bed = prefs.stopBeforeBedMinutes;
  const sleep =
    waking === bed
      ? waking
        ? `${formatPlanningMinutes(waking)} around sleep`
        : null
      : waking && bed
      ? `${formatPlanningMinutes(waking)} / ${formatPlanningMinutes(bed)} around sleep`
      : waking
      ? `${formatPlanningMinutes(waking)} after waking`
      : `${formatPlanningMinutes(bed)} before bed`;
  return [`${formatPlanningMinutes(prefs.deepBreakMinutes)} after deep work`, sleep].filter(Boolean).join(' · ');
};

// When and which days: two parts at most, like every closed row (Petr). The daily maximum is inside the row.
export const summarizePlanningSchedule = (prefs: PlanningPreferences, firstDayOfWeek?: number | null) => {
  const time = prefs.preferredTime.length
    ? prefs.preferredTime.map(name => PLANNING_TIME_OF_DAY_PLURAL[name]).join(' & ')
    : // With none picked, deep work leans to late morning and afternoon, then evening: not any time at all.
      'Daytime first';
  const off = prefs.daysOff.length
    ? `off ${planningWeekdayOptions(firstDayOfWeek)
        .filter(option => prefs.daysOff.includes(option.value))
        .map(option => option.label)
        .join(', ')}`
    : 'no days off';
  const text = [time, off].join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** Counts the listed courses with settings of their own: a rule for another semester's course is not shown. */
/**
 * The semester's courses in the student's saved order. A course added since goes at the end, and one from another
 * semester is left out.
 */
export const planningCourseOrder = <T extends { id: number }>(courseOrder: number[], courses: T[]): T[] => {
  const place = new Map(courseOrder.map((id, index) => [id, index]));
  return courses
    .map((course, index) => ({ course, key: place.has(course.id) ? place.get(course.id)! : courseOrder.length + index }))
    .sort((a, b) => a.key - b.key)
    .map(item => item.course);
};

export const summarizePlanningCourses = (prefs: PlanningPreferences, courses: { id: number; name: string }[]) => {
  const courseIds = courses.map(course => course.id);
  const set = prefs.courseRules.filter(rule => courseIds.includes(rule.courseId) && hasCourseRule(rule)).length;
  const first = prefs.orderCourses ? planningCourseOrder(prefs.courseOrder, courses)[0] : undefined;
  const text = [first ? `${first.name} first` : null, set ? `preferences for ${set} course${set === 1 ? '' : 's'}` : null]
    .filter(Boolean)
    .join(' · ');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Same for every course';
};

// The toast after planning. Details were in the dialog's preview, so this only confirms the outcome.
/**
 * The toast after planning: only what went on the calendar (Petr). Not what didn't fit: the preview showed that
 * before the click, the Cushion keeps flagging those tasks, and a success with a shortfall in one amber toast read as
 * a failure. No "replan" and no offer to either: right after planning a student hasn't had time to look at the plan.
 * The dialog won't plan when nothing would go on the calendar, so "Nothing was planned." is only for a calendar that
 * changed between the preview and the run.
 */
export const formatPlanResult = (plannedMinutes: number) =>
  plannedMinutes === 0 ? 'Nothing was planned.' : `Planned ${formatPlanningMinutes(plannedMinutes)}.`;

export const togglePromptChip = (current: string, instruction: string) => {
  const lines = current
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  if (lines.includes(instruction)) {
    return lines.filter(line => line !== instruction).join('\n');
  }

  return [...lines, instruction].join('\n');
};

export const isPromptChipSelected = (current: string, instruction: string) =>
  current
    .split('\n')
    .map(line => line.trim())
    .includes(instruction);

/**
 * Planned blocks the backend would actually remove for a range: they start inside it, are still ahead, and
 * are not marked as used. Mirrors `PlannerService.unplan` so the dialog counts match what unplanning does.
 */
export const getUnplannableBlocks = <T extends { start: string | Date; isUsed?: boolean }>(
  plannedEvents: { [id: string]: T },
  from: Date,
  to: Date
): T[] => {
  const currentTime = now();
  const rangeStart = toMomentDate(from);
  const rangeEnd = toMomentDate(to);

  return Object.values(plannedEvents).filter(event => {
    const start = toMomentDate(event.start);
    return !event.isUsed && start.isAfter(currentTime) && !start.isBefore(rangeStart) && start.isBefore(rangeEnd);
  });
};

/**
 * How far back the missed list reaches for blocks these dates don't already cover. The backend stops counting a
 * block toward `timePlanned` the moment it ends unused, so its minutes are back in what Auto-plan schedules whichever
 * dates are being planned — a block missed before them would otherwise be planned again with no row offering to mark
 * it used, and a ramp would step back. A week covers the plan the student made last time, whichever day they plan on
 * (Petr: two days, and only for dates starting today or tomorrow, left Monday to Thursday out of a Sunday plan and
 * everything out of one made on a Friday). Longer would keep a block the student really skipped on screen for weeks:
 * there is no "I skipped it" to clear it with.
 */
export const MISSED_LOOKBACK_DAYS = 7;

/**
 * Where the missed list starts: these dates' own start, or midnight `days` days back when that is earlier. Only the
 * listing reaches back — what Auto-plan schedules still comes from the tasks in these dates.
 */
export const missedLookbackStart = (
  from: Date,
  timeZone: string,
  currentTime: Moment = now(),
  days: number = MISSED_LOOKBACK_DAYS
): Date => {
  const lookback = currentTime
    .clone()
    .tz(timeZone)
    .startOf('day')
    .subtract(days, 'days');
  return lookback.isBefore(from) ? lookback.toDate() : from;
};

/**
 * Blocks in the range that ended without being marked used, for tasks still to plan. The backend stops counting a
 * block toward `timePlanned` once it ends unused, so its minutes are already back in what Auto-plan schedules.
 */
export const getMissedBlocks = <T extends { start: string | Date; end: string | Date; isUsed?: boolean; taskId?: number }>(
  plannedEvents: { [id: string]: T },
  from: Date,
  to: Date,
  taskIds: Set<number>,
  currentTime: Moment = now()
): T[] =>
  Object.values(plannedEvents).filter(
    event =>
      !event.isUsed &&
      event.taskId != null &&
      taskIds.has(event.taskId) &&
      !toMomentDate(event.end).isAfter(currentTime) &&
      toMomentDate(event.end).isAfter(from) &&
      toMomentDate(event.start).isBefore(to)
  );

/**
 * The tasks a plan just gave sessions to. Their missed sessions come off the calendar after it: their time is in the
 * new plan, and a Log time on one would credit the task a second time.
 */
export const plannedTaskIds = (sessions: { taskId?: number | null }[]): number[] =>
  Array.from(new Set(sessions.map(session => session.taskId).filter((taskId): taskId is number => taskId != null)));

/**
 * How far back the Overdue list reaches: the last week, the plan the student made last time. Auto-plan never plans
 * after a due date, so work that slipped past one would drop out of the plan unasked; older than this, the task list's
 * own Overdue section has it.
 */
export const OVERDUE_LOOKBACK_DAYS = 7;

/** The first day the Overdue list covers, as the endpoint takes it: midnight a week back, in the semester's time zone. */
export const overdueFrom = (timeZone: string, currentTime: Moment = now(), days: number = OVERDUE_LOOKBACK_DAYS) =>
  currentTime
    .clone()
    .tz(timeZone)
    .startOf('day')
    .subtract(days, 'days')
    .format(DATE_FORMAT);

/** A task past its due date with time still to do, as the Auto-plan dialog lists it. */
export type OverdueRow = {
  taskId: number;
  title: string;
  courseId?: number;
  courseName?: string;
  /** "Oct 3, 11:59pm" (or "Oct 3, 23:59"), in the semester's time zone. */
  due: string;
  /** What the task still needs. */
  minutesLeft: number;
  cardTime: CardTime;
};

/** The Overdue rows, soonest due first. The list is named as the task list names its own (Petr). */
export const describeOverdue = (
  tasks: (Pick<TaskPlanningInfo, 'id' | 'title' | 'courseId' | 'courseName' | 'dueDate' | 'timeNeed'> & CardTimeFields)[],
  timeZone: string
): OverdueRow[] =>
  tasks
    .map(task => ({ task, dueAt: toMomentDate(task.dueDate) }))
    .sort((a, b) => a.dueAt.valueOf() - b.dueAt.valueOf() || a.task.title.localeCompare(b.task.title))
    .map(({ task, dueAt }) => ({
      taskId: task.id,
      title: task.title,
      courseId: task.courseId,
      courseName: task.courseName,
      due: dueDayLabel(dueAt.tz(timeZone)).short,
      minutesLeft: task.timeNeed,
      cardTime: cardTimeOf(task, task.timeNeed)
    }));

/** One past block, as the Auto-plan dialog lists it so the student can say they used its time after all. */
export type MissedBlockRow = {
  plannedTaskId: number;
  taskId: number;
  title: string;
  courseId?: number;
  courseName?: string;
  /** "Oct 29, 7:00am – 8:30pm" — when the block was, in the semester's own timezone, written like the due dates beside it. */
  when: string;
  /** Minutes to credit: what the timer already holds, or the block's own length. */
  minutes: number;
  /** True once it has been marked used, which is what keeps the row on screen with its tick filled in. */
  isUsed: boolean;
  /** Pages already recorded against the block, for a reading. */
  pages: number | null;
  /** Set for a reading, which is credited in pages rather than minutes. */
  reading?: {
    sourceName?: string;
    secondsPerPage: number;
    pagesLeft: number | null;
    /**
     * What the block's time comes to at this source's rate, never more than is left to read. Offered as the
     * field's placeholder so marking a block done needs no typing, and a student who read a different amount
     * types over an empty field rather than clearing a number first.
     */
    suggestedPages: number | null;
  };
};

/**
 * How many pages a block's time comes to at the source's own rate, capped at what is left to read.
 *
 * Null when the block is too short to amount to a page: a suggestion of none is worse than none at all, and
 * the student can still say what they read.
 */
/**
 * The minutes a reading session's pages are guessed from, which are the minutes ✓ logs: its timer's time while it has
 * a minute or more on it, else the time already logged, else the session's length. The plan's length alone credited the
 * plan's pages for less time read, and the book's speed came out wrong (Petr, 2026-10-31).
 */
export const minutesForPagesGuess = (timerSeconds: number | null, loggedMinutes: number, sessionMinutes: number) => {
  if (timerSeconds != null && timerSeconds >= SECONDS_IN_A_MINUTE) return Math.floor(timerSeconds / SECONDS_IN_A_MINUTE);
  return loggedMinutes > 0 ? loggedMinutes : sessionMinutes;
};

export const suggestPagesRead = (minutes: number, secondsPerPage: number, pagesLeft: number | null) => {
  if (minutes <= 0 || secondsPerPage <= 0) return null;
  const pages = Math.round((minutes * SECONDS_IN_A_MINUTE) / secondsPerPage);
  if (pages <= 0) return null;
  return pagesLeft != null && pagesLeft > 0 ? Math.min(pages, pagesLeft) : pages;
};

/**
 * Describes the past blocks for the dialog's list.
 *
 * `workloadTasks` is what tells a reading apart from a plain task — the planner endpoint doesn't say, the same
 * reason describeUnestimated takes it. `tasksInRange` carries the pages still to read, so a reading row can show
 * what is left rather than making the student guess whether they finished it.
 */
export const describeMissedBlocks = (
  blocks: {
    id: string | number;
    taskId?: number;
    start: string | Date;
    end: string | Date;
    used?: number;
    isUsed?: boolean;
    pages?: number | null;
  }[],
  timeZone: string,
  workloadTasks: Pick<WorkloadTask, 'id' | 'reading'>[] = [],
  tasksInRange: Pick<TaskPlanningInfo, 'id' | 'title' | 'courseId' | 'courseName' | 'pagesLeft'>[] = []
): MissedBlockRow[] => {
  const workloadById = new Map(workloadTasks.map(task => [task.id, task]));
  const inRangeById = new Map(tasksInRange.map(task => [task.id, task]));

  return blocks
    .filter(block => block.taskId != null && inRangeById.has(block.taskId))
    .map(block => ({ block, startsAt: toMomentDate(block.start).tz(timeZone) }))
    .sort((a, b) => a.startsAt.valueOf() - b.startsAt.valueOf())
    .map(({ block, startsAt }) => {
      const task = inRangeById.get(block.taskId!)!;
      const endsAt = toMomentDate(block.end).tz(timeZone);
      // The server credits time already logged on the timer in preference to the block's length, so the row has
      // to offer the same number rather than the length it can see.
      const timerMinutes = block.used || 0;
      const reading = workloadById.get(block.taskId!)?.reading;
      return {
        plannedTaskId: Number(block.id),
        taskId: block.taskId!,
        title: task.title,
        courseId: task.courseId,
        courseName: task.courseName,
        when: pastSessionLabel(startsAt, endsAt),
        minutes: timerMinutes > 0 ? timerMinutes : endsAt.diff(startsAt, 'minutes'),
        isUsed: !!block.isUsed,
        pages: block.pages ?? null,
        reading: reading && {
          sourceName: reading.name,
          secondsPerPage: reading.secondsPerPage || DEFAULT_SECONDS_PER_PAGE,
          pagesLeft: task.pagesLeft,
          suggestedPages: suggestPagesRead(
            timerMinutes > 0 ? timerMinutes : endsAt.diff(startsAt, 'minutes'),
            reading.secondsPerPage || DEFAULT_SECONDS_PER_PAGE,
            task.pagesLeft
          )
        }
      };
    });
};

// Mirrors PlannerService.isStillShovelPlanned: an Auto-plan block the student has since edited is theirs.
export const isShovelPlanned = (block: { autoPlanned?: boolean; editedByStudent?: boolean }) =>
  !!block.autoPlanned && !block.editedByStudent;

export type PlanStudyBlock = {
  id: number;
  start: Date;
  end: Date;
};

export const clipStudyBlocksToNow = (
  blocks: { start: Date; end: Date }[],
  currentTime: Moment = now()
): PlanStudyBlock[] => {
  const clipped: PlanStudyBlock[] = [];
  blocks.forEach(block => {
    const start = toMomentDate(block.start);
    const end = toMomentDate(block.end);
    if (!end.isAfter(currentTime, 'minute')) {
      return;
    }
    clipped.push({
      id: clipped.length,
      start: start.isBefore(currentTime) ? currentTime.toDate() : start.toDate(),
      end: end.toDate()
    });
  });
  return clipped;
};

export const taskEarliestStart = (
  dueDate: Date | string,
  daysToStartAhead: number | null | undefined,
  timeZone: string,
  currentTime: Moment = now(),
  studyBlocks: PlanStudyBlock[] = []
): Date => {
  const startOfWindow = toMomentDate(dueDate)
    .tz(timeZone)
    .subtract(daysToStartAhead || 0, 'days')
    .startOf('day');
  let earliest = startOfWindow.isAfter(currentTime) ? startOfWindow : currentTime.clone();
  const due = toMomentDate(dueDate);
  const nextBlock = studyBlocks
    .map(block => ({ start: toMomentDate(block.start), end: toMomentDate(block.end) }))
    .filter(block => block.end.isAfter(earliest) && block.start.isBefore(due))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf())[0];
  if (nextBlock && nextBlock.start.isAfter(earliest)) {
    earliest = nextBlock.start;
  }
  return earliest.toDate();
};

/**
 * The study blocks a task may use: everything that starts before it's due. Blocks before the task's start date are
 * included on purpose - the start date is a rule the student can have Auto-plan drop, and filtering here would settle
 * that before the scheduler ever weighs it. The scheduler holds the start date while the rule is on.
 */
export const taskBlockIds = (dueDate: Date | string, studyBlocks: PlanStudyBlock[]): number[] => {
  const due = toMomentDate(dueDate);
  return studyBlocks.filter(block => toMomentDate(block.start).isBefore(due)).map(block => block.id);
};

type TimeRange = { start: string | Date; end: string | Date };

/**
 * The dates review sessions are made for: the days Auto-plan is planning, the last being the day before its end, as the
 * dialog's title reads it (`planRangeEnds`). The preview and the plan ask for the same ones, so what the student saw is
 * what gets made, and a review that falls after these days waits for the plan that covers it.
 */
export const reviewDates = (from: Date, to: Date) => ({
  from: toMomentDate(from).format(DATE_FORMAT),
  to: toMomentDate(to)
    .subtract(1, 'day')
    .format(DATE_FORMAT)
});

/**
 * A review the preview shows before it exists needs an id the scheduler can plan it under. Below zero, so it can never
 * be a real task's, and one per reading and review, so the plan can swap in the real id once it is made.
 */
export const reviewVirtualId = (reviewOfTaskId: number, reviewNumber: number) => -(reviewOfTaskId * 10 + reviewNumber);

export const reviewPlanningTask = (review: MissingReview): TaskPlanningInfo => ({
  ...review,
  id: reviewVirtualId(review.reviewOfTaskId, review.reviewNumber)
});

/**
 * The request's tasks with each previewed review under the id it was just saved with. A review that was not made -
 * already there by now, or no longer due these dates - is left out rather than planned under an id that is no task.
 */
export const withCreatedReviewIds = <T extends { id: number }>(tasks: T[], created: TaskPlanningInfo[]): T[] => {
  const ids = new Map(
    created
      .filter(review => review.reviewOfTaskId != null && review.reviewNumber != null)
      .map(review => [reviewVirtualId(review.reviewOfTaskId!, review.reviewNumber!), review.id])
  );
  return tasks.flatMap(task => {
    if (task.id > 0) return [task];
    const id = ids.get(task.id);
    return id ? [{ ...task, id }] : [];
  });
};

/**
 * What Auto-plan sends for the visible range: future study time, the estimated tasks whose planning window
 * overlaps it (with their remaining minutes), and study already planned there. Shared by the dialog and by
 * Replan, which rebuilds it after unplanning so the freed time and minutes are planned again.
 */
export const buildPlanInputs = ({
  freeTimes,
  busyEvents,
  plannedEvents,
  awakeTimes,
  eventEnds,
  tasksInRange,
  timeZone,
  from,
  to,
  allBusyEvents,
  currentTime = now()
}: {
  freeTimes: (TimeRange & { type: CalendarEventType })[];
  busyEvents: (TimeRange & { type: CalendarEventType; title?: string; courseName?: string; courseId?: number })[];
  plannedEvents: { [id: string]: TimeRange };
  /** The student's awake times: the scheduler keeps study away from waking up and bedtime. */
  awakeTimes: TimeRange[];
  /** Where each busy event ends, unless a commute follows it: study starts a break after it. */
  eventEnds: (Date | string)[];
  tasksInRange: TaskPlanningInfo[];
  timeZone: string;
  from: Date;
  to: Date;
  /** The AI planner (typed instructions, testing only) reads every busy event; the scheduler only classes. */
  allBusyEvents: boolean;
  currentTime?: Moment;
}) => {
  const studyBlocks = clipStudyBlocksToNow(
    freeTimes
      .filter(event => event.type === CalendarEventType.STUDY_TIME)
      .map(event => ({ start: event.start as Date, end: event.end as Date })),
    currentTime
  );
  const candidates = tasksInRange
    .filter(task => !!task.timeNeed && (!task.timePlanned || task.timePlanned < task.timeNeed))
    .map(task => {
      const earliestStart = taskEarliestStart(task.dueDate, task.daysToStartAhead, timeZone, currentTime, studyBlocks);
      return {
        id: task.id,
        title: task.title,
        dueDate: task.dueDate,
        earliestStart,
        timeNeed: task.timeNeed - (task.timePlanned || 0),
        progressMinutes: (task.timeSpent || 0) + (task.timePlanned || 0),
        // A review's days are Shovel's own, so it isn't counted against when the student likes to finish.
        isReview: task.reviewOfTaskId != null,
        priority: task.priority,
        courseName: task.courseName,
        courseId: task.courseId,
        blockIds: taskBlockIds(task.dueDate, studyBlocks)
      };
    });
  const tasks = candidates.filter(task => task.blockIds.length > 0);
  // Tasks due in these dates with no free study time left before they're due can't be planned at all. They're kept out
  // of the request (the scheduler needs a block to place them) but reported, so the dialog can say they won't fit.
  // A task due after these dates without time here isn't stuck: its work belongs to the later dates.
  const noTimeTasks = candidates
    .filter(task => task.blockIds.length === 0 && !toMomentDate(task.dueDate).isAfter(to))
    .map(task => ({ taskId: task.id, minutes: task.timeNeed }));
  // Only the AI planner (typed instructions, testing only) reads busy events; the scheduler does not.
  const calendarEvents = (allBusyEvents ? busyEvents : []).map(event => ({
    title: event.title,
    type: event.type,
    start: event.start,
    end: event.end,
    courseName: event.courseName,
    courseId: event.courseId
  }));
  // Study already on the calendar counts toward the daily maximum.
  const plannedBlocks = Object.values(plannedEvents || {})
    .filter(event => toMomentDate(event.end).isAfter(from) && toMomentDate(event.start).isBefore(to))
    .map(event => ({ start: event.start, end: event.end }));
  // Awake times that reach into these dates, so their waking up and bedtime both travel.
  const awakeTimesInRange = awakeTimes
    .filter(awake => toMomentDate(awake.end).isAfter(from) && toMomentDate(awake.start).isBefore(to))
    .map(awake => ({ start: awake.start, end: awake.end }));
  const eventEndsInRange = eventEnds.filter(end => toMomentDate(end).isAfter(from) && toMomentDate(end).isBefore(to));

  return {
    studyBlocks,
    tasks,
    calendarEvents,
    plannedBlocks,
    awakeTimes: awakeTimesInRange,
    eventEnds: eventEndsInRange,
    noTimeTasks
  };
};

/**
 * The dialog's picture of the range once Replan has unplanned `blocks`: their time is free again and their minutes
 * are back on their tasks. A preview only — the saga rebuilds the real request from the calendar after unplanning.
 */
export const withBlocksUnplanned = (
  freeTimes: (TimeRange & { type: CalendarEventType })[],
  tasksInRange: TaskPlanningInfo[],
  blocks: (TimeRange & { taskId?: number })[]
): { freeTimes: (TimeRange & { type: CalendarEventType })[]; tasksInRange: TaskPlanningInfo[] } => {
  if (blocks.length === 0) return { freeTimes, tasksInRange };

  const ranges = [...freeTimes.filter(event => event.type === CalendarEventType.STUDY_TIME), ...blocks]
    .map(range => ({ start: toMomentDate(range.start), end: toMomentDate(range.end) }))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());
  const merged: { start: Moment; end: Moment }[] = [];
  ranges.forEach(range => {
    const last = merged[merged.length - 1];
    if (last && !range.start.isAfter(last.end)) {
      if (range.end.isAfter(last.end)) last.end = range.end;
    } else {
      merged.push({ ...range });
    }
  });

  const freedMinutes: { [taskId: number]: number } = {};
  blocks.forEach(block => {
    if (block.taskId == null) return;
    const minutes = toMomentDate(block.end).diff(toMomentDate(block.start), 'minutes');
    freedMinutes[block.taskId] = (freedMinutes[block.taskId] || 0) + minutes;
  });

  return {
    freeTimes: merged.map(range => ({
      type: CalendarEventType.STUDY_TIME,
      start: range.start.toDate(),
      end: range.end.toDate()
    })),
    tasksInRange: tasksInRange.map(task =>
      freedMinutes[task.id] ? { ...task, timePlanned: Math.max(0, (task.timePlanned || 0) - freedMinutes[task.id]) } : task
    )
  };
};

/**
 * Every minute the task needed was planned and used: what the Used tag says. Its sidebar card keeps its footer open, to
 * mark it complete or add time, so the sidebar makes that card's row taller too.
 */
export const isTaskFullyUsed = (task: { timeNeed?: number; timePlanned?: number; timeSpent?: number }) =>
  (task.timeSpent ?? 0) > 0 && (task.timeNeed ?? 0) <= 0 && (task.timePlanned ?? 0) <= 0;
